import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Loader2, Trash2, UploadCloud, ImagePlus, Check, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useToast } from "../../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const resolveSrc = (s) => (s && s.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${s}` : s);

export default function GalleryManager({ token }) {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [cat, setCat] = useState("");
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);
  const { toast } = useToast();

  const headers = { "X-Admin-Token": token };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/gallery`, { headers });
      setItems(res.data.items || []);
      setCats(res.data.categories || []);
      if (!cat && res.data.categories?.length) setCat(res.data.categories[0]);
    } catch {
      toast({ title: "Failed to load photos" });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    e.preventDefault();
    if (!file) { toast({ title: "Choose an image first" }); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("cat", cat || cats[0] || "Pool Enclosures");
    try {
      const res = await axios.post(`${API}/admin/gallery`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      setItems((prev) => [...prev, res.data]);
      setFile(null); setTitle("");
      if (fileRef.current) fileRef.current.value = "";
      toast({ title: "Photo added", description: "It's now live on the gallery page." });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || "Please try again." });
    } finally {
      setUploading(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove "${item.title}" from the gallery?`)) return;
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    try {
      await axios.delete(`${API}/admin/gallery/${item.id}`, { headers });
      toast({ title: "Photo removed" });
    } catch {
      toast({ title: "Delete failed" });
      load();
    }
  };

  const saveEdit = async () => {
    const { id, title: t, cat: c } = editing;
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, title: t, cat: c } : i)));
    setEditing(null);
    try {
      await axios.patch(`${API}/admin/gallery/${id}`, { title: t, cat: c }, { headers });
    } catch {
      toast({ title: "Update failed" });
      load();
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload card */}
      <form onSubmit={onUpload} data-testid="gallery-upload-form" className="rounded-2xl bg-white p-6 ring-1 ring-brand-mist shadow-card">
        <div className="flex items-center gap-2 mb-4">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand-ocean/10 text-brand-ocean"><ImagePlus className="h-5 w-5" /></span>
          <div>
            <h3 className="font-heading font-extrabold text-brand-navy">Add a Photo</h3>
            <p className="text-xs text-brand-slate">JPG, PNG or WEBP · up to 15MB</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-xs font-heading font-bold text-brand-slate uppercase tracking-wide">Image file</label>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="gallery-file-input"
              className="mt-1.5 block w-full text-sm text-brand-navy file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:bg-brand-ocean file:text-white file:font-heading file:font-bold file:cursor-pointer hover:file:bg-brand-sky rounded-lg ring-1 ring-brand-mist"
            />
          </div>
          <div>
            <label className="text-xs font-heading font-bold text-brand-slate uppercase tracking-wide">Category</label>
            <select
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              data-testid="gallery-category-select"
              className="mt-1.5 block w-full h-11 px-3 rounded-lg ring-1 ring-brand-mist text-sm text-brand-navy bg-white focus:outline-none focus:ring-2 focus:ring-brand-ocean"
            >
              {cats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-heading font-bold text-brand-slate uppercase tracking-wide">Title / caption</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Screened Pool with Paver Deck" data-testid="gallery-title-input" className="mt-1.5 h-11" />
          </div>
        </div>
        {file && (
          <div className="mt-4 flex items-center gap-3 text-sm text-brand-slate">
            <img src={URL.createObjectURL(file)} alt="preview" className="h-16 w-16 rounded-lg object-cover ring-1 ring-brand-mist" />
            <span className="truncate">{file.name}</span>
          </div>
        )}
        <Button type="submit" disabled={uploading || !file} data-testid="gallery-upload-btn" className="mt-5 h-11 bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-extrabold">
          {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading…</> : <><UploadCloud className="h-4 w-4 mr-2" /> Add to Gallery</>}
        </Button>
      </form>

      {/* Grid */}
      <div className="rounded-2xl bg-white p-6 ring-1 ring-brand-mist shadow-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-extrabold text-brand-navy">Gallery Photos</h3>
          <span className="text-sm text-brand-slate">{items.length} photos</span>
        </div>
        {loading ? (
          <div className="py-16 grid place-items-center text-brand-slate"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : items.length === 0 ? (
          <p className="py-16 text-center text-brand-slate text-sm">No photos yet. Add your first one above.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
            {items.map((item) => (
              <div key={item.id} className="group relative rounded-xl overflow-hidden ring-1 ring-brand-mist bg-brand-mist/40">
                <img src={resolveSrc(item.src)} alt={item.title} loading="lazy" className="w-full aspect-square object-cover" />
                {editing?.id === item.id ? (
                  <div className="p-3 space-y-2 bg-white">
                    <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="h-9 text-sm" />
                    <select value={editing.cat} onChange={(e) => setEditing({ ...editing, cat: e.target.value })} className="w-full h-9 px-2 rounded-md ring-1 ring-brand-mist text-sm bg-white">
                      {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <div className="flex gap-2">
                      <Button onClick={saveEdit} className="flex-1 h-8 bg-brand-ocean hover:bg-brand-sky text-white text-xs"><Check className="h-4 w-4 mr-1" /> Save</Button>
                      <Button onClick={() => setEditing(null)} variant="ghost" className="h-8 text-brand-slate text-xs"><X className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="p-2.5">
                      <div className="text-xs font-heading font-bold text-brand-navy truncate">{item.title}</div>
                      <div className="text-[11px] text-brand-slate">{item.cat}</div>
                    </div>
                    <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => setEditing({ id: item.id, title: item.title, cat: item.cat })} aria-label="Edit" data-testid={`gallery-edit-${item.id}`} className="h-8 w-8 grid place-items-center rounded-lg bg-white/90 text-brand-navy hover:bg-white shadow">
                        <ImagePlus className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(item)} aria-label="Delete" data-testid={`gallery-delete-${item.id}`} className="h-8 w-8 grid place-items-center rounded-lg bg-red-500/90 text-white hover:bg-red-600 shadow">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
